import React, { useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { CollectionReptile, CollectionReptilePicture } from '../types/CollectionTypes.ts'
import { PhotoUploader } from '../../utilities/PhotoUploader.tsx';
import { BasicFetch } from '../../../utils/BasicFetch.ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faTimes } from '@fortawesome/free-solid-svg-icons';
import { ReptoolModal } from '../../utilities/ReptoolModal.tsx';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useRepToolContext } from '../../../RepToolContext.tsx';

import { driver } from 'driver.js';

export const setGridControls = (appendControls: (component: JSX.Element | null, currentComponent: string) => void,  rearrangeActive: boolean, setRearrangeActive: (active: boolean) => void, orderChanged: boolean, readonly: boolean) => {
  if (!readonly) {
    appendControls(
      <div className='d-flex flex-row w-100 justify-content-center'>
        {!rearrangeActive ? <Button id='rearrange-btn' size='lg' variant='outline-light' className='w-100' onClick={() => setRearrangeActive(true)}>Rearrange</Button> :
          <Button id='save-btn' size='lg' variant='outline-light' className='w-100' onClick={() => setRearrangeActive(false)} >
            {orderChanged ? 'Save Order' : 'Cancel'}
          </Button>}
      </div>, 'Edit Photos Grid Controls'
    )
  } else {
    appendControls(null, 'Edit Photos Grid Controls readonly');
  }
}



const SortableItem = ({
  item,
  rearrangeOrder
}: {
  item: CollectionReptilePicture,
  rearrangeOrder: CollectionReptilePicture[]
}) => {

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'none',
  };

  return (
    <div ref={setNodeRef} className='text-dark position-relative border my-2' style={style} {...attributes} {...listeners}  >
      <div className='m-2' style={{
        minHeight: 100,
        backgroundImage: `url(${item.url})`,
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'

      }}>
      </div>
      <div className='bg-light border border-secondary text-secondary text-center position-absolute rounded' style={{ top: -5, right: -5, width: 30, height: 30 }} >
        {
          rearrangeOrder.findIndex((image) => image.id === item.id) + 1
        }
      </div>
    </div>
  )
}


export const EditPhotosGrid = ({
  maxPhotos,
  pictures,
  readonly = false,
  createFunction,
  deleteFunction,
  uploadingPicture = false
}: {
  pictures: {
    id: number,
    url: string
  }[]
  maxPhotos: number,
  readonly?: boolean,
  createFunction: (photo: Blob) => void,
  deleteFunction: (id: number) => void,
  uploadingPicture: boolean
}) => {

  return (
    <div className="d-flex flex-row flex-wrap justify-content-center mt-4" style={{ gap: 20 }}>
      {pictures.map((image, index) => (
        <div key={image.id} className="position-relative" style={{ flex: '40%', width: '100%' }}>
          <div className='border rounded' style={{
            minHeight: 200,
            height: '100%',
            width: '100%',
            backgroundImage: `url(${image.url})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}>
          </div>
          {!readonly && <div className='bg-light border border-secondary text-secondary text-center position-absolute rounded' style={{ top: -5, right: -5, width: 30, height: 30 }} >
            <FontAwesomeIcon icon={faTimes} onClick={() => deleteFunction(image.id)} />
          </div>}
        </div>
      ))}
      {!readonly && pictures.length < maxPhotos && <div style={{ flex: '40%', width: '100%' }}>
        <PhotoUploader setUploadPhoto={(photo) => createFunction(photo)}
          uploadText='Upload Additional Photo'
          clearPhoto={!uploadingPicture}
        />
        {uploadingPicture && <div className='text-center text-secondary'>Uploading...</div>}
      </div>}
      {!readonly && pictures.length >= maxPhotos && <div className='border' style={{ flex: '40%', width: '100%' }}>
        <div className='d-flex flex-column justify-content-center h-100 p-3'>
          <div className='text-center text-light mb-4'>
            <FontAwesomeIcon icon={faCircleInfo} size='2x' />
          </div>
          <div className='text-center text-light'>
            You have reached the maximum number of photos for this animal.
            {maxPhotos === 1 ? ' The maximum number of photos for reptiles outside of your collection is 1.' : ''}

          </div>
        </div>
      </div>}

    </div>)
}

export const SortAndUploadPhotos = ({
  pictures,
  rearrangeActive,
  rearrangeOrder,
  setRearrangeOrder,
  saveOrder,
  readonly = false,
  createFunction,
  deleteFunction,
  uploadingPicture,
  maxPhotos = 6
}: {
  pictures: {
    id: number,
    url: string
  }[],
  rearrangeActive: boolean,
  rearrangeOrder: CollectionReptilePicture[],
  setRearrangeOrder: (photos: CollectionReptilePicture[]) => void,
  saveOrder: () => void,
  readonly?: boolean,
  createFunction: (photo: Blob) => void,
  deleteFunction: (id: number) => void,
  uploadingPicture: boolean,
  maxPhotos?: number
}) => {


  useEffect(() => {
    setRearrangeOrder(pictures);
  }, [pictures]);
  useEffect(() => {
    if (!rearrangeActive && pictures !== rearrangeOrder) {
      saveOrder();
    }

  }, [rearrangeActive])

  return (
    <>
      {rearrangeActive ? <DndContext collisionDetection={closestCenter} onDragEnd={({ active, over }) => {
        if (active && over) {
          const oldIndex = rearrangeOrder.findIndex((image) => image.id === active.id);
          const newIndex = rearrangeOrder.findIndex((image) => image.id === over.id);
          setRearrangeOrder(arrayMove(rearrangeOrder, oldIndex, newIndex));
        }
      }
      }>
        <SortableContext items={rearrangeOrder.map((image) => image.id)}>
          <div className="mt-4" style={{ gap: 20 }}>
            {rearrangeOrder.map((image) => (
              <SortableItem key={image.id} item={image} rearrangeOrder={rearrangeOrder} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
        :
        <EditPhotosGrid
          uploadingPicture={uploadingPicture}
          readonly={readonly}
          createFunction={createFunction}
          pictures={pictures}
          deleteFunction={deleteFunction}
          maxPhotos={maxPhotos}
        />}
    </>
  )
}

export const EditPhotosModal = ({
  reptile,
  setReptile,
  closeModal,
  readonly = false
}: {
  reptile: CollectionReptile,
  setReptile: (reptile: CollectionReptile) => void,
  closeModal: () => void,
  readonly?: boolean
}) => {
  // create a modal to reorder or upload 6 photos
  const [rearrangeActive, setRearrangeActive] = React.useState(false);
  const [rearrangeOrder, setRearrangeOrder] = React.useState(reptile.pictures);
  const [uploadingPicture, setUploadPicture] = React.useState(false);
  const [pendingPicture, setPendingPicture] = React.useState<Blob | null>(null);
  const pictures = reptile.pictures;
  const { appendControls } = useRepToolContext();

  const orderChanged = pictures !== rearrangeOrder;

  const driverObj = driver({
    steps: [
      { element: '#edit-photos-modal', popover: { title: 'Edit Photos', description: 'Here you can rearrange the order of your photos or upload new ones.' } },
      { element: '#photos-grid', popover: { title: 'Photos', description: 'These are all the photos uploaded for this animal. You can upload a maximum of 6.' } },
      !rearrangeActive ? { element: '#rearrange-btn', popover: { title: 'Rearrange', description: 'Click here to rearrange the order of your photos.' } }
        : { element: '#save-btn', popover: { title: 'Save Order', description: 'Click here to save your new photo order.' } }

    ]
  })

  useEffect(() => {
    setRearrangeOrder(reptile.pictures);
  }, [reptile.pictures]);

  useEffect(() => {
    setGridControls(appendControls, rearrangeActive, setRearrangeActive, orderChanged, readonly);
  }, [rearrangeOrder, rearrangeActive, pictures])

  const saveOrder = () => {
    const data = { new_order: rearrangeOrder.map((image) => image.id), new_serialize: 'true' }
    BasicFetch({
      url: `${(window as any).serverLocation}/collection_reptile_photo/${reptile.id}/update_priorities`,
      method: 'PATCH',
      data: data,
      contentType: 'application/json',
      onSuccess: (data: { reptile: CollectionReptile }) => {
        setReptile(data.reptile);
      },
      onFail: () => {
        window.alert("There was a problem saving your photo order. Please try again later.")
      }
    })
  }
  const removePicture = (id) => {
    if (readonly) return;
    setUploadPicture(true);
    BasicFetch({
      url: `${(window as any).serverLocation}/collection_reptile_photo/${reptile.id}/${id}/delete`,
      data: {},
      extra: {
        new_serialize: 'true'
      },
      method: 'Delete',
      onSuccess: (data) => {
        setReptile(data.reptile);
      },
      onFail: () => {
        window.alert("There was a problem removing your photo. Please try again later.")
      },
      onFinish: () => {
        setUploadPicture(false);
      }
    })
  }
  const uploadPhoto = (photo: Blob) => {
    if (!photo) return;
    if (readonly) return;
    let formData = new FormData();
    let filename = `${new Date().toISOString()}.jpg`
    setUploadPicture(true);
    formData.append("file", photo, filename);
    formData.append("new_serialize", "true")

    BasicFetch({
      url: `${(window as any).serverLocation}/collection_reptile_photo/${reptile.id}/create`,
      data: formData,
      method: 'POST',
      contentType: 'multipart/form-data',
      onSuccess: (data) => {
        setReptile(data.reptile);
        setPendingPicture(null);
      },
      onFail: () => {
        window.alert("There was a problem uploading your photo. Please try again later.")
      },
      onFinish: () => {
        setUploadPicture(false);
      }
    })
  }


  return (
    <ReptoolModal id={"edit-photos-modal"} title={"Edit Photos"} showTutorial={driverObj.drive} closeModal={closeModal} body={
      <div id='photos-grid' className='pb-5'>
        <SortAndUploadPhotos
          pictures={reptile.pictures}
          rearrangeActive={rearrangeActive}
          rearrangeOrder={rearrangeOrder}
          setRearrangeOrder={setRearrangeOrder}
          saveOrder={saveOrder}
          readonly={readonly}
          createFunction={uploadPhoto}
          deleteFunction={removePicture}
          uploadingPicture={uploadingPicture}
        />

        <div className='pb-5'></div>
      </div>
    } />
  )
}